import * as THREE from 'three';
import {hasDoors} from 'components/customer/RoomPlanner/helpers/doorDetection';
import {getGroupWidth} from 'components/customer/RoomPlanner/helpers/calculations';
import {
    CabinetWallContext,
    MeasurementConfig,
    RoomConfig,
    WALL_OFFSETS,
} from 'components/customer/RoomPlanner/helpers/measurement/types';

/**
 * Build the shared context needed for wall distance calculations.
 * Extracts room dimensions, cabinet position/gaps, and door thickness
 * so both the distance and inverse functions share the same setup.
 *
 * @param {THREE.Group} cabinetGroup - The cabinet group to measure from
 * @param {RoomConfig} roomConfig - Room dimensions and type
 * @return {CabinetWallContext} The computed context
 */
export const buildCabinetWallContext = (
    cabinetGroup: THREE.Group,
    roomConfig: RoomConfig
): CabinetWallContext => {
    const {roomType, rectangularDimension, lShapedDimension, scale} =
        roomConfig;
    const {width, depth} = getGroupWidth(cabinetGroup);
    const position = cabinetGroup.position;
    const currentDirection =
        (cabinetGroup.userData.currentDirection as string) || 'front'; // Room dimensions
    const roomWidth =
        roomType === 'RECTANGULAR'
            ? rectangularDimension.width
            : lShapedDimension.rightWidth;
    const roomLength =
        roomType === 'RECTANGULAR'
            ? rectangularDimension.length
            : lShapedDimension.leftWidth;
    const effectiveRoomLength =
        roomType === 'LSHAPED' ? lShapedDimension.rightDepth : roomLength;
    const effectiveRoomWidth =
        roomType === 'LSHAPED' ? lShapedDimension.leftDepth : roomWidth * scale;
    const rightDepth = roomType === 'LSHAPED' ? lShapedDimension.rightDepth : 0; // Cabinet positions and gaps

    const cabinetHasDoors = hasDoors(cabinetGroup);
    const upperLshaped =
        (cabinetGroup.userData.isUpperProduct as boolean) &&
        (cabinetGroup.userData.isLShapedProduct as boolean);
    const openTallLShape =
        (cabinetGroup.userData.isTallLShape as boolean) &&
        (cabinetGroup.userData.isLShapedProduct as boolean);
    const doorThickness = cabinetHasDoors
        ? (
              cabinetGroup.userData.values as {
                  cabinet_ext_thickness?: number;
              }
          )?.cabinet_ext_thickness * scale || 0
        : WALL_OFFSETS.noDoorThickness +
          (upperLshaped || openTallLShape ? WALL_OFFSETS.lShapedBulkhead : 0);
    const isLShapedProduct = !!(
        cabinetGroup.userData as {isLShapedProduct?: boolean}
    )?.isLShapedProduct;

    return {
        width,
        depth,
        position,
        currentDirection,
        roomWidth,
        roomLength,
        effectiveRoomLength,
        effectiveRoomWidth,
        rightDepth,
        doorThickness,
        scale,
        roomType,
        isLShapedProduct,
        cabinetHasDoors,
        cabinetGroup,
        lShapedDimension,
    };
};

/**
 * Convert a MeasurementConfig into the RoomConfig expected by
 * wall distance helpers.
 *
 * @param {MeasurementConfig} config - Measurement configuration
 * @return {RoomConfig | null} Room config or null if scale is missing
 */
export const toRoomConfig = (config: MeasurementConfig): RoomConfig | null => {
    if (!config.scale) return null;

    const isLShaped = !!(config.leftWidth && config.rightWidth);

    return {
        roomType: isLShaped ? 'LSHAPED' : 'RECTANGULAR',
        rectangularDimension: {
            width: config.roomWidth || 0,
            length: config.roomLength || 0,
            height: config.wallHeight,
        },
        lShapedDimension: {
            rightWidth: config.rightWidth || config.roomWidth || 0,
            leftWidth: config.leftWidth || config.roomLength || 0,
            rightDepth: config.rightDepth || 0,
            leftDepth: config.leftDepth || 0,
            height: config.wallHeight,
        },
        scale: config.scale,
    };
};
